// Phone photos weigh 3-10 MB. The backend scales every image down to fit
// 2000x2600 before embedding it in the receipt PDF, so doing the same in the
// browser loses nothing and keeps uploads small: fast on mobile data and under
// the reverse proxy's request size limit.
const MAX_WIDTH = 2000;
const MAX_HEIGHT = 2600;
const JPEG_QUALITY = 0.85;
// Below this size a JPEG/PNG is uploaded as is.
const SMALL_IMAGE_BYTES = 500 * 1024;

const RESIZABLE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/bmp",
];

const isHeic = (file: File): boolean =>
  ["image/heic", "image/heif"].includes(file.type.toLowerCase()) ||
  /\.hei[cf]$/i.test(file.name);

const canvasToJpeg = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));

/**
 * Returns a resized JPEG copy of an image file, or the file itself when it is
 * not an image, already small, or cannot be decoded by this browser (the
 * backend then validates it and reports a clear error).
 */
export const compressImage = async (file: File): Promise<File> => {
  const heic = isHeic(file);
  if (!heic && !RESIZABLE_TYPES.includes(file.type.toLowerCase())) {
    return file;
  }
  if (!heic && file.size <= SMALL_IMAGE_BYTES) {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (error) {
    // Not decodable here (e.g. HEIC outside Safari): upload the original and
    // let the backend accept or reject it with an explicit message.
    console.error(`Impossible de compresser l'image ${file.name} :`, error);
    return file;
  }

  const scale = Math.min(
    MAX_WIDTH / bitmap.width,
    MAX_HEIGHT / bitmap.height,
    1,
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    return file;
  }
  // JPEG has no transparency: paint transparent PNG areas white, not black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await canvasToJpeg(canvas);
  // HEIC is always converted; other formats only when it actually helps.
  if (!blob || (!heic && blob.size >= file.size)) {
    return file;
  }

  const baseName = file.name.replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
};
